"use client";

import { useEffect } from "react";
import Image from "next/image";
import { Box, Divider, Tooltip, Typography } from "@mui/material";

const CROSSMARK_CONFIG = {
    VERIFICATION: "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpYXQiOjE2NjY1MDE5NDIsInZlciI6IjIuMi4yIn0.Y0BGMBQd81otZafJBR85JPGLuLaEwoctmNA-p3l1euw",
    ENDPOINT: "https://crossmark.crossref.org/dialog",
    SCRIPT_VERSION: "2.2.2",
    STYLESHEET_URL: "https://crossmark-cdn.crossref.org/widget/v2.0/style.css",
    LOGO_URL: "https://crossmark-cdn.crossref.org/images/logo-crossmark.svg"
};

const isIOS = () => {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
    ].includes(navigator.platform)
        || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
};

const ArticleCrossmark = ({ doi }: { doi: string }) => {
    useEffect(() => {
        const link = document.createElement("link");
        link.href = CROSSMARK_CONFIG.STYLESHEET_URL;
        link.type = "text/css";
        link.rel = "stylesheet";
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const handleShow = () => {
        const existingOverlay = document.querySelector(".crossmark-overlay");
        if (existingOverlay) {
            existingOverlay.parentNode?.removeChild(existingOverlay);
        }

        const params = {
            doi: doi,
            domain: window.location.hostname,
            uri_scheme: window.location.protocol,
            cm_version: CROSSMARK_CONFIG.SCRIPT_VERSION,
            verification: CROSSMARK_CONFIG.VERIFICATION
        };

        const queryString = Object.entries(params)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`)
            .join("&");

        const container = document.createElement("div");
        container.id = "crossmark-widget";
        container.innerHTML = `
      <div class="crossmark-reset crossmark-overlay"></div>
      <div class="crossmark-reset crossmark-popup">
        <div class="crossmark-reset crossmark-popup__offset ${isIOS() ? 'is-ios' : ''}">
          <div class="crossmark-reset crossmark-popup__inner">
            <div class="crossmark-reset crossmark-popup__header">
              <a target="_blank" href="http://www.crossref.org/crossmark">
                <img class="crossmark-reset crossmark-popup__logo" src="${CROSSMARK_CONFIG.LOGO_URL}">
              </a>
              <button class="crossmark-reset crossmark-popup__btn-close"></button>
            </div>
            <div class="crossmark-reset crossmark-popup__content-wrapper">
              <iframe class="crossmark-reset crossmark-popup__content" src="${CROSSMARK_CONFIG.ENDPOINT}?${queryString}"></iframe>
            </div>
          </div>
        </div>
      </div>
    `;

        document.body.appendChild(container);

        // Add event listeners
        const overlay = container.querySelector(".crossmark-overlay");
        const popup = container.querySelector(".crossmark-popup");
        const closeBtn = container.querySelector(".crossmark-popup__btn-close");
        const inner: any = container.querySelector(".crossmark-popup__inner");

        const closeDialog = (event: any) => {
            container.style.display = "none";
            event.preventDefault();
            event.stopPropagation();
        };

        [overlay, popup, closeBtn].forEach(element => {
            element?.addEventListener("click", closeDialog);
        });

        inner?.addEventListener("click", (event: any) => {
            event.stopPropagation();
        });

        container.style.display = "block";

        if (isIOS() && inner) {
            inner.style.top = `${window.scrollY}px`;
        }
    };

    return (
        <Box mt={1}>
            <Divider sx={{ opacity: .25 }} />
            <Box
                display="flex"
                alignItems="flex-start"
                mt={1}
                onClick={handleShow}
                sx={{ cursor: "pointer" }}
            >
                <Tooltip title={<strong>Crossmark</strong>} arrow placement="bottom">
                    <Image
                        src="https://crossmark-cdn.crossref.org/widget/v2.0/logos/CROSSMARK_Color_square_no_text.svg"
                        alt="Crossmark"
                        width={55}
                        height={55}
                        quality={100}
                        style={{ width: 55, height: 55, marginRight: 8 }}
                    />
                </Tooltip>
                <Box>
                    <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                    >
                        Crossmark
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="green"
                    >
                        Checking
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default ArticleCrossmark;